
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserRound, Award } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Article } from '@/data/articles';
import { useNavigate } from 'react-router-dom';

interface ValuedPeopleCardProps {
  article: Article;
  className?: string;
  enhancedImage?: boolean;
  onClick?: () => void;
}

// Custom verified badge SVG component
const VerifiedBadge = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="16" 
    height="16" 
    viewBox="0 0 48 48"
    className="ml-2"
  >
    <polygon fill="#42a5f5" points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"></polygon>
    <polygon fill="#fff" points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"></polygon>
  </svg>
);

const ValuedPeopleCard: React.FC<ValuedPeopleCardProps> = ({ article, className, enhancedImage = false, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  // Higher quality people images for each card
  const getPeopleImage = (authorName: string) => {
    // Map author names to specific high-quality images
    const imageMap: Record<string, string> = {
      // Leadership team
      "Elena Mitchel": "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop",
      "Samantha Lee": "https://images.unsplash.com/photo-1649972904349-6e44c42644a7?q=80&w=2070&auto=format&fit=crop",
      "Sophie Taylor": "/Images/ramaya nagendar.jpg",
      "Priya Sharma": "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=1974&auto=format&fit=crop",
      "Nora Patel": "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1961&auto=format&fit=crop",
      "Laura Kim": "https://images.unsplash.com/photo-1598550874175-4d0ef436c909?q=80&w=1970&auto=format&fit=crop",
      "Marcus Johnson": "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?q=80&w=1974&auto=format&fit=crop",
      "Thomas Wright": "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?q=80&w=1974&auto=format&fit=crop",
      "James Rodriguez": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop",
      "David Chen": "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=1974&auto=format&fit=crop",
      "Mark Wilson": "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop",
      "Rafael Gomez": "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=1974&auto=format&fit=crop",
      
      // Talented professionals (regular team members)
      "Alex Morgan": "https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=1964&auto=format&fit=crop",
      "Michael Chang": "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?q=80&w=1964&auto=format&fit=crop",
      "Sarah Johnson": "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1974&auto=format&fit=crop",
      "Emily Chen": "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?q=80&w=1960&auto=format&fit=crop",
      "Jordan Taylor": "https://images.unsplash.com/photo-1507152832244-10d45c7eda57?q=80&w=1974&auto=format&fit=crop",
      "Alisha Patel": "https://images.unsplash.com/photo-1589156280159-27698a70f29e?q=80&w=1972&auto=format&fit=crop",
      "Chris Martinez": "https://images.unsplash.com/photo-1508341591423-4347099e1f19?q=80&w=1974&auto=format&fit=crop",
      "Leila Wong": "https://images.unsplash.com/photo-1573496799652-408c2ac9fe98?q=80&w=1976&auto=format&fit=crop",
      "Daniel Brown": "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=1974&auto=format&fit=crop",
      "Olivia Garcia": "https://images.unsplash.com/photo-1601412436009-d964bd02edbc?q=80&w=1964&auto=format&fit=crop",
      "Jason Lee": "https://images.unsplash.com/photo-1531891437562-4301cf35b7e4?q=80&w=1964&auto=format&fit=crop",
      "Sophia Williams": "https://images.unsplash.com/photo-1558898479-33c0057a5d12?q=80&w=1970&auto=format&fit=crop",
    };
    
    return imageMap[authorName] || article.imageUrl;
  };

  // Get designation based on the author name - updated to display the article title as the designation
  const getDesignation = (authorName: string) => {
    // Use the article title as the designation instead of the hardcoded designations
    return article.title;
  };

  // Check if this is a highly valued person
  const isHighlyValued = article.priority === 'high';

  // Handle button click
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else {
      navigate(`/achievers?id=${article.id}`);
    }
  };

  return (
    <Card 
      className={cn(
        "relative overflow-hidden h-full transition-all duration-300 border-none rounded-xl group",
        isHighlyValued 
          ? "hover:shadow-2xl hover:translate-y-[-12px] bg-white" 
          : "hover:shadow-lg hover:translate-y-[-5px]",
        enhancedImage && isHighlyValued && "shadow-xl",
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <CardContent className="p-0 flex flex-col h-full">
        {/* Full-width image with premium styling for highly valued people - FIXED PADDING HERE */}
        <div className={cn(
          "w-full h-72 relative overflow-hidden",
          isHighlyValued && enhancedImage ? "ring-4 ring-amber-300 ring-offset-2" : 
          isHighlyValued ? "ring-2 ring-amber-200 ring-offset-1" : ""
        )}>
          <div className="w-full h-full">
            <img 
              src={enhancedImage ? getPeopleImage(article.author.name) : article.imageUrl}
              alt={article.author.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/placeholder.svg';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          
          {/* Enhanced overlay gradient for Top Achievers */}
          {isHighlyValued && (
            <div className={cn(
              "absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-70 transition-opacity duration-300",
              enhancedImage && "from-black/40 via-transparent to-black/10",
              isHovered && "opacity-60"
            )}></div>
          )}

          {/* Award badge for Top Achievers */}
          {isHighlyValued && enhancedImage && (
            <div className="absolute top-3 right-3 bg-amber-500 text-white p-1.5 rounded-full shadow-lg transform rotate-12 z-10">
              <Award className="h-5 w-5" />
            </div>
          )}
          
          {/* Special highlight effect for highly valued people when hovered */}
          {isHighlyValued && enhancedImage && (
            <div className={cn(
              "absolute inset-0 bg-gradient-to-r from-purple-500/10 to-amber-500/10 opacity-0 transition-opacity duration-500",
              isHovered && "opacity-60"
            )}></div>
          )}
        </div>
        
        <div className={cn(
          "p-4 flex flex-col justify-between flex-1",
          isHighlyValued && enhancedImage ? "bg-gradient-to-b from-white to-purple-50" :
          isHighlyValued && "bg-gradient-to-b from-white to-purple-50/30" 
        )}>
          {/* Name and designation with enhanced styling */}
          <div className="mb-4">
            <h3 className={cn(
              "text-xl font-bold tracking-tight leading-tight mb-1 flex items-center",
              isHighlyValued && "text-purple-900"
            )}>
              {article.author.name}
              {isHighlyValued && enhancedImage && (
                <VerifiedBadge />
              )}
            </h3>
            <p className={cn(
              "text-sm truncate",
              isHighlyValued ? "text-purple-700" : "text-muted-foreground"
            )}>
              {getDesignation(article.author.name)}
            </p>
          </div>
          
          {/* Action button with premium styling for highly valued people */}
          <div className="w-full mt-auto">
            <Button 
              variant={isHighlyValued ? "profile" : "default"}
              className={cn(
                "w-full h-10 shadow-sm transition-all",
                isHighlyValued && enhancedImage ? 
                  "bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-white hover:from-amber-600 hover:to-amber-700 shadow-lg hover:shadow-xl" : 
                isHighlyValued ? 
                  "bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 shadow-md hover:shadow-lg" : 
                  "bg-primary hover:bg-primary/90"
              )}
              onClick={handleButtonClick}
            >
              <UserRound className="h-4 w-4 mr-2" /> 
              {isHighlyValued ? "Read Success Story" : "Read Success Story"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ValuedPeopleCard;
